import type { ComponentProps, ReactNode } from 'react';
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { StatTile } from '../../../components/ui/Card';
import { formatDayMonth } from '../../../utils/dateFormat';
import { formatCompact, formatNumber } from '../../../utils/numberFormat';
import {
  CHART_AXIS,
  CHART_GRID,
  CHART_HEIGHT,
  CHART_SERIES,
  CHART_TOOLTIP,
  ChartCard,
} from '../../../components/ui/chart';
import { withoutPartialToday } from './period';

// The top of CSM › Usage, one structure for both platforms so Classic and aOS
// can't drift apart: three tiles (Active users, Tokens, Active customers), an
// optional Credits tile after Tokens, an optional roster bridge, then three
// daily charts in the same order. Each platform passes its own numbers and says
// what "active" means in the tile subtitles.

export interface UsageOverviewDay {
  date: string;
  tokens: number;
  activeUsers: number;
  activeCompanies: number;
}

type TileProps = Pick<ComponentProps<typeof StatTile>, 'value' | 'change' | 'sub' | 'tooltip'>;

export interface UsageOverviewProps {
  activeUsers: TileProps;
  tokens: TileProps;
  /** Credits as the customer's platform shows them; only aOS has them. */
  credits?: TileProps;
  activeCustomers: TileProps;
  /** The roster bridge, directly under the tiles. */
  bridge?: ReactNode;
  series: readonly UsageOverviewDay[];
  /** Chart titles carry the platform where the number is platform-specific. */
  tokensTitle?: string;
}

function formatDay(value: string): string {
  return formatDayMonth(value.length === 10 ? `${value}T00:00:00Z` : value);
}

function dailyChart(data: readonly UsageOverviewDay[], dataKey: keyof UsageOverviewDay, color: string, name: string) {
  return (
    <AreaChart data={withoutPartialToday(data)} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid {...CHART_GRID} />
      <XAxis dataKey="date" tickFormatter={(v) => formatDay(String(v))} {...CHART_AXIS} minTickGap={32} />
      <YAxis tickFormatter={(v) => formatCompact(Number(v))} {...CHART_AXIS} width={52} tickCount={4} />
      <Tooltip
        {...CHART_TOOLTIP}
        formatter={(value) => [formatNumber(Number(value ?? 0)), name] as [string, string]}
        labelFormatter={(label) => formatDay(String(label))}
      />
      <Area
        type="linear"
        dataKey={dataKey}
        stroke={color}
        strokeWidth={2}
        fill={color}
        fillOpacity={0.12}
        dot={false}
        activeDot={{ r: 4 }}
        isAnimationActive={false}
      />
    </AreaChart>
  );
}

export function UsageOverview({ activeUsers, tokens, credits, activeCustomers, bridge, series, tokensTitle = 'Daily tokens' }: UsageOverviewProps) {
  const note = 'Today is left out until the day is complete';
  const empty = series.length === 0;
  return (
    <div className="space-y-6">
      <div className={`grid grid-cols-1 gap-4 ${credits ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
        <StatTile label="Active users" {...activeUsers} />
        <StatTile label="Tokens" {...tokens} />
        {credits && <StatTile label="Credits" {...credits} />}
        <StatTile label="Active customers" {...activeCustomers} />
      </div>

      {bridge}

      <div className="grid gap-6 lg:grid-cols-3">
        <ChartCard
          title={tokensTitle}
          description={note}
          height={CHART_HEIGHT.small}
          isEmpty={empty}
          emptyLabel="No token activity in this period."
          chart={dailyChart(series, 'tokens', CHART_SERIES[0], 'Tokens')}
        />
        <ChartCard
          title="Daily active users"
          description={note}
          height={CHART_HEIGHT.small}
          isEmpty={empty}
          emptyLabel="No active users in this period."
          chart={dailyChart(series, 'activeUsers', CHART_SERIES[2], 'Active users')}
        />
        <ChartCard
          title="Daily active customers"
          description={note}
          height={CHART_HEIGHT.small}
          isEmpty={empty}
          emptyLabel="No active customers in this period."
          chart={dailyChart(series, 'activeCompanies', CHART_SERIES[1], 'Active customers')}
        />
      </div>
    </div>
  );
}
